import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Building, X, ArrowLeft, Upload, Megaphone, Pencil, KeyRound, UserPlus, Copy, Power, CalendarPlus } from "lucide-react";
import api, { apiError } from "../lib/api";
import { buildIcs, downloadIcs } from "../lib/ics";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const btnDark = "bg-slate-900 text-white font-bold py-2.5 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50";
const smallBtn = "inline-flex items-center gap-1.5 text-xs font-bold border border-slate-200 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50";

function Modal({ title, onClose, children, wide, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`bg-white rounded-2xl p-6 w-full ${wide ? "max-w-2xl" : "max-w-md"} my-8 space-y-3`} data-testid={testId}>
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button type="button" onClick={onClose} data-testid={`${testId}-close`}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function LogoField({ url, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/organisations/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Logo uploaded");
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };
  return (
    <div>
      <label className="text-xs font-bold text-slate-500">Corporate logo (optional)</label>
      <div className="flex items-center gap-3 mt-1">
        {url ? <img src={url} alt="logo" className="h-12 w-12 rounded-lg object-contain border border-slate-200 bg-white" data-testid="org-logo-preview" />
             : <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400"><Building size={20} /></div>}
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className={smallBtn} data-testid="org-logo-upload-btn">
          <Upload size={12} /> {busy ? "Uploading…" : url ? "Replace logo" : "Upload logo"}
        </button>
        {url && <button type="button" onClick={() => onChange(null)} className="text-xs text-red-500 font-medium hover:underline">Remove</button>}
        <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={pick} data-testid="org-logo-file-input" />
      </div>
    </div>
  );
}

function OrgFormModal({ org, onSaved, onClose }) {
  const [f, setF] = useState({ name: org?.name || "", contact_name: org?.contact_name || "",
    contact_email: org?.contact_email || "", seat_limit: org?.seat_limit || 10, notes: org?.notes || "", logo_url: org?.logo_url || null });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f, seat_limit: parseInt(f.seat_limit, 10) || 1 };
      if (org) await api.put(`/admin/organisations/${org.id}`, payload);
      else await api.post("/admin/organisations", payload);
      toast.success(org ? "Organisation updated" : "Organisation created");
      onSaved(); onClose();
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };
  return (
    <Modal title={org ? "Edit organisation" : "Create organisation"} onClose={onClose} testId="org-form-modal">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-xs font-bold text-slate-500">Organisation name *</label>
          <input className={inputCls} required value={f.name} onChange={set("name")} data-testid="org-form-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold text-slate-500">Contact person</label>
            <input className={inputCls} value={f.contact_name} onChange={set("contact_name")} data-testid="org-form-contact" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Contact email</label>
            <input className={inputCls} type="email" value={f.contact_email} onChange={set("contact_email")} data-testid="org-form-email" />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500">Seat limit *</label>
          <input className={inputCls} type="number" min="1" required value={f.seat_limit} onChange={set("seat_limit")} data-testid="org-form-seats" />
        </div>
        <LogoField url={f.logo_url} onChange={(u) => setF({ ...f, logo_url: u })} />
        <div>
          <label className="text-xs font-bold text-slate-500">Contract notes (private)</label>
          <textarea className={inputCls} rows={2} value={f.notes} onChange={set("notes")} placeholder="e.g. 12-month contract signed 20 Jun 2026, invoiced annually" data-testid="org-form-notes" />
        </div>
        <button type="submit" disabled={busy} className={`w-full ${btnDark}`} data-testid="org-form-save">
          {busy ? "Saving…" : org ? "Save changes" : "Create organisation"}
        </button>
      </form>
    </Modal>
  );
}

function copyText(text, label) {
  navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
}

function UploadModal({ org, onDone, onClose }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/organisations/${org.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(r.data);
      onDone();
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };
  const generated = report?.created?.filter((c) => c.generated_password) || [];
  const copyAll = () => copyText(generated.map((c) => `${c.email}: ${c.generated_password}`).join("\n"), "Passwords");
  return (
    <Modal title={`Bulk upload — ${org.name}`} onClose={onClose} wide testId="org-upload-modal">
      {!report ? (
        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600 space-y-1.5">
            <p className="font-bold text-slate-800">CSV format (save your Excel sheet as CSV):</p>
            <p>Required columns: <strong>name</strong>, <strong>email</strong></p>
            <p>Optional columns: <strong>job title</strong>, <strong>password</strong> (temporary — min 6 characters; left blank = auto-generated for you)</p>
            <p>Everyone is created as <strong>Enterprise Pro</strong> and must change their password on first login.</p>
            <p>Seats: {org.seats_used}/{org.seat_limit} used.</p>
          </div>
          <button onClick={() => ref.current?.click()} disabled={busy} className={`w-full ${btnDark}`} data-testid="org-upload-pick-btn">
            {busy ? "Uploading & creating accounts…" : "Choose CSV file"}
          </button>
          <input ref={ref} type="file" accept=".csv" className="hidden" onChange={pick} data-testid="org-upload-file-input" />
        </div>
      ) : (
        <div className="space-y-4" data-testid="org-upload-report">
          <div className="flex gap-3 text-sm font-bold">
            <span className="text-emerald-700">{report.created.length} created</span>
            <span className="text-amber-600">{report.skipped.length} skipped</span>
            <span className="text-red-500">{report.errors.length} errors</span>
            <span className="text-slate-400 font-medium ml-auto">Seats: {report.seats_used}/{report.seat_limit}</span>
          </div>
          {generated.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold text-emerald-800">Auto-generated temporary passwords — copy them now (shown once):</p>
                <button onClick={copyAll} className={smallBtn} data-testid="org-upload-copy-passwords"><Copy size={12} /> Copy all</button>
              </div>
              <div className="max-h-44 overflow-y-auto text-xs font-mono text-slate-700 space-y-1">
                {generated.map((c) => <p key={c.email}>{c.email} — {c.generated_password}</p>)}
              </div>
            </div>
          )}
          {report.skipped.length > 0 && (
            <div className="text-xs text-slate-500 max-h-24 overflow-y-auto">
              {report.skipped.map((s) => <p key={s.row}>Row {s.row}: {s.email} — {s.reason}</p>)}
            </div>
          )}
          {report.errors.length > 0 && (
            <div className="text-xs text-red-500 max-h-24 overflow-y-auto">
              {report.errors.map((s) => <p key={s.row}>Row {s.row}: {s.email} — {s.reason}</p>)}
            </div>
          )}
          <button onClick={onClose} className={`w-full ${btnDark}`} data-testid="org-upload-done">Done</button>
        </div>
      )}
    </Modal>
  );
}

function AddEmployeeModal({ org, onSaved, onClose }) {
  const [f, setF] = useState({ name: "", job_title: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [genPwd, setGenPwd] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/admin/organisations/${org.id}/members`, { ...f, password: f.password || null });
      onSaved();
      if (r.data.generated_password) setGenPwd(r.data.generated_password);
      else { toast.success("Employee added"); onClose(); }
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };
  return (
    <Modal title={`Add employee — ${org.name}`} onClose={onClose} testId="org-member-add-modal">
      {genPwd ? (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Account created. Temporary password (shown once):</p>
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3">
            <code className="text-sm font-mono flex-1" data-testid="org-member-gen-password">{genPwd}</code>
            <button onClick={() => copyText(`${f.email}: ${genPwd}`, "Password")} className={smallBtn}><Copy size={12} /> Copy</button>
          </div>
          <button onClick={onClose} className={`w-full ${btnDark}`}>Done</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-bold text-slate-500">Full name *</label>
            <input className={inputCls} required value={f.name} onChange={set("name")} data-testid="org-member-add-name" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Job title</label>
            <input className={inputCls} value={f.job_title} onChange={set("job_title")} data-testid="org-member-add-job" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Email *</label>
            <input className={inputCls} type="email" required value={f.email} onChange={set("email")} data-testid="org-member-add-email" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Temporary password (optional)</label>
            <input className={inputCls} value={f.password} onChange={set("password")} placeholder="Leave blank to auto-generate" data-testid="org-member-add-password" />
          </div>
          <button type="submit" disabled={busy} className={`w-full ${btnDark}`} data-testid="org-member-add-save">
            {busy ? "Creating…" : "Add employee"}
          </button>
        </form>
      )}
    </Modal>
  );
}

function EditEmployeeModal({ member, onSaved, onClose }) {
  const [f, setF] = useState({ name: member.name || "", email: member.email || "", job_title: member.job_title || "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/members/${member.id}`, f);
      toast.success("Employee updated");
      onSaved(); onClose();
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };
  return (
    <Modal title="Edit employee" onClose={onClose} testId="org-member-edit-modal">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-xs font-bold text-slate-500">Full name</label>
          <input className={inputCls} required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="org-member-edit-name" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500">Job title</label>
          <input className={inputCls} value={f.job_title} onChange={(e) => setF({ ...f, job_title: e.target.value })} data-testid="org-member-edit-job" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500">Email</label>
          <input className={inputCls} type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="org-member-edit-email" />
        </div>
        <button type="submit" disabled={busy} className={`w-full ${btnDark}`} data-testid="org-member-edit-save">{busy ? "Saving…" : "Save"}</button>
      </form>
    </Modal>
  );
}

function SetPasswordModal({ member, onClose }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/org-members/${member.id}/password`, { password: pwd });
      toast.success("Temporary password set — they must change it at next login");
      onClose();
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };
  return (
    <Modal title={`Set temporary password — ${member.name || member.email}`} onClose={onClose} testId="org-member-pwd-modal">
      <form onSubmit={submit} className="space-y-3">
        <input className={inputCls} required minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="New temporary password (min 6 characters)" data-testid="org-member-pwd-input" />
        <p className="text-xs text-slate-500">The employee will be required to change it the next time they log in.</p>
        <button type="submit" disabled={busy} className={`w-full ${btnDark}`} data-testid="org-member-pwd-save">{busy ? "Saving…" : "Set password"}</button>
      </form>
    </Modal>
  );
}

function BroadcastModal({ org, onClose }) {
  const [f, setF] = useState({ subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/admin/organisations/${org.id}/broadcast`, f);
      toast.success(`Update sent to ${r.data.recipients} member${r.data.recipients === 1 ? "" : "s"}`);
      onClose();
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };
  return (
    <Modal title={`Broadcast update — ${org.name}`} onClose={onClose} testId="org-broadcast-modal">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-xs font-bold text-slate-500">Subject *</label>
          <input className={inputCls} required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="org-broadcast-subject" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500">Message *</label>
          <textarea className={inputCls} rows={5} required value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="e.g. We've just added 35 new career & promotion commands to your dictionary…" data-testid="org-broadcast-message" />
        </div>
        <p className="text-xs text-slate-500">Sent as a branded email to every active member of this organisation.</p>
        <button type="submit" disabled={busy} className={`w-full ${btnDark}`} data-testid="org-broadcast-send">{busy ? "Sending…" : "Send update"}</button>
      </form>
    </Modal>
  );
}

function OrgDetail({ org, onBack, onOrgsChanged }) {
  const [members, setMembers] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editFor, setEditFor] = useState(null);
  const [pwdFor, setPwdFor] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/admin/organisations/${org.id}/members`);
      setMembers(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [org.id]);

  const toggleMember = async (m) => {
    const activating = m.active === false;
    if (!window.confirm(activating ? `Reactivate ${m.email}?` : `Deactivate ${m.email}? They will no longer be able to log in.`)) return;
    setBusyId(m.id);
    try { await api.put(`/admin/org-members/${m.id}/status`, { active: activating }); await load(); toast.success(activating ? "Reactivated" : "Deactivated"); }
    catch (e) { toast.error(apiError(e)); }
    setBusyId(null);
  };

  const toggleOrg = async () => {
    const activating = org.status !== "active";
    if (!window.confirm(activating ? `Reactivate ${org.name}? All its employees regain access.` : `Deactivate ${org.name}? ALL its employees lose access immediately.`)) return;
    try { await api.put(`/admin/organisations/${org.id}/status`, { active: activating }); toast.success(activating ? "Organisation reactivated" : "Organisation deactivated"); onBack(); onOrgsChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="org-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-slate-900 mb-4" data-testid="org-back-btn">
        <ArrowLeft size={15} /> All organisations
      </button>
      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 mb-5">
        {org.logo_url ? <img src={org.logo_url} alt={org.name} className="h-14 w-14 rounded-xl object-contain border border-slate-100" />
                      : <div className="h-14 w-14 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400"><Building size={24} /></div>}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-lg">{org.name}</h3>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${org.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>{org.status}</span>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            {members.length}/{org.seat_limit} seats
            {org.contact_name && <> • {org.contact_name}</>}
            {org.contact_email && <> • {org.contact_email}</>}
          </p>
          {org.notes && <p className="text-xs text-slate-400 mt-0.5">{org.notes}</p>}
        </div>
        <div className="flex gap-2 flex-wrap shrink-0">
          <button onClick={() => setShowEdit(true)} className={smallBtn} data-testid="org-edit-btn"><Pencil size={12} /> Edit</button>
          <button onClick={() => setShowBroadcast(true)} className={smallBtn} data-testid="org-broadcast-btn"><Megaphone size={12} /> Broadcast</button>
          <button onClick={toggleOrg} className={`${smallBtn} ${org.status === "active" ? "border-red-200 text-red-600 hover:bg-red-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`} data-testid="org-status-toggle">
            <Power size={12} /> {org.status === "active" ? "Deactivate org" : "Reactivate org"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button onClick={() => setShowUpload(true)} className="bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-xl hover:bg-emerald-800 transition-colors inline-flex items-center gap-2" data-testid="org-upload-open">
          <Upload size={14} /> Bulk upload (CSV)
        </button>
        <button onClick={() => setShowAdd(true)} className="bg-slate-900 text-white text-sm font-bold px-4 py-2 rounded-xl hover:bg-slate-800 transition-colors inline-flex items-center gap-2" data-testid="org-member-add-open">
          <UserPlus size={14} /> Add employee
        </button>
      </div>

      <div className="space-y-2.5">
        {members.map((m) => (
          <div key={m.id} className={`bg-white border rounded-2xl p-4 flex flex-col md:flex-row md:items-center gap-3 ${m.active === false ? "border-red-100 opacity-70" : "border-slate-200"}`} data-testid={`org-member-row-${m.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold">{m.name || "—"}</p>
                {m.job_title && <span className="text-xs text-slate-500">{m.job_title}</span>}
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${m.active === false ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-800"}`}>
                  {m.active === false ? "Deactivated" : "Active"}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{m.email}</p>
            </div>
            <div className="flex gap-2 shrink-0 flex-wrap">
              <button onClick={() => setEditFor(m)} className={smallBtn} data-testid={`org-member-edit-${m.id}`}><Pencil size={12} /> Edit</button>
              <button onClick={() => setPwdFor(m)} className={smallBtn} data-testid={`org-member-pwd-${m.id}`}><KeyRound size={12} /> Set password</button>
              <button onClick={() => toggleMember(m)} disabled={busyId === m.id}
                className={`${smallBtn} ${m.active === false ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-red-200 text-red-600 hover:bg-red-50"}`}
                data-testid={`org-member-toggle-${m.id}`}>
                <Power size={12} /> {m.active === false ? "Reactivate" : "Deactivate"}
              </button>
            </div>
          </div>
        ))}
        {members.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No employees yet — use bulk upload or add one manually.</p>}
      </div>

      {showUpload && <UploadModal org={{ ...org, seats_used: members.length }} onDone={load} onClose={() => setShowUpload(false)} />}
      {showAdd && <AddEmployeeModal org={org} onSaved={load} onClose={() => setShowAdd(false)} />}
      {showBroadcast && <BroadcastModal org={org} onClose={() => setShowBroadcast(false)} />}
      {showEdit && <OrgFormModal org={org} onSaved={onOrgsChanged} onClose={() => setShowEdit(false)} />}
      {editFor && <EditEmployeeModal member={editFor} onSaved={load} onClose={() => setEditFor(null)} />}
      {pwdFor && <SetPasswordModal member={pwdFor} onClose={() => setPwdFor(null)} />}
    </div>
  );
}

const STATUS_STYLES = { new: "bg-amber-100 text-amber-800", contacted: "bg-sky-100 text-sky-800", closed: "bg-slate-100 text-slate-500" };

function InquiriesPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = async () => {
    try { setItems((await api.get("/admin/enterprise-inquiries")).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/enterprise-inquiries/${id}`, { status }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try { await api.delete(`/admin/enterprise-inquiries/${id}`); toast.success("Enquiry deleted"); setOpen(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (items.length === 0) {
    return (
      <div className="mb-8 bg-white border border-dashed border-slate-200 rounded-2xl p-5 text-sm text-slate-500" data-testid="enterprise-inquiries-empty">
        <p className="font-display font-bold text-slate-700 mb-1">Enterprise enquiries</p>
        No submissions yet. Form responses from <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">/enterprise</span> will appear here with callback times, contact details, and status tracking.
      </div>
    );
  }
  const newCount = items.filter((q) => q.status === "new").length;

  return (
    <div className="mb-8 bg-white border border-slate-200 rounded-2xl p-5" data-testid="enterprise-inquiries-panel">
      <h3 className="font-display font-bold flex items-center gap-2">
        Enterprise enquiries
        {newCount > 0 && <span className="text-[10px] font-bold uppercase bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">{newCount} new</span>}
      </h3>
      <div className="mt-3 divide-y divide-slate-100">
        {items.map((q) => (
          <div key={q.id} className="py-3" data-testid={`inquiry-row-${q.id}`}>
            <button type="button" className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 text-left" onClick={() => setOpen(open === q.id ? null : q.id)}>
              <span className="font-bold text-sm">{q.company_name}</span>
              <span className="text-xs text-slate-500">{q.contact_person}{q.job_title && ` · ${q.job_title}`}</span>
              <span className="text-xs text-slate-400">{q.team_size} staff{q.country && ` · ${q.country}`}</span>
              <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_STYLES[q.status] || STATUS_STYLES.new}`}>{q.status}</span>
            </button>
            {open === q.id && (
              <div className="mt-3 bg-slate-50 rounded-xl p-4 text-sm space-y-2" data-testid={`inquiry-detail-${q.id}`}>
                <p><span className="text-slate-500">Email:</span> <a className="text-emerald-700 font-semibold" href={`mailto:${q.business_email}`}>{q.business_email}</a>{q.phone && <span className="text-slate-500"> · {q.phone}</span>}</p>
                <div className="flex flex-wrap gap-2">
                  <a data-testid={`inquiry-reply-${q.id}`}
                    href={`mailto:${q.business_email}?subject=${encodeURIComponent("Re: Your enquiry at XpressPrompts — " + (q.company_name || ""))}&body=${encodeURIComponent(`Hi ${q.contact_person || "there"},\n\nThank you for your enquiry — I'd be delighted to arrange a short call to walk you through XpressPrompts for ${q.company_name || "your team"}.\n\n`)}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700">
                    ✉ Reply by email
                  </a>
                  {q.matched_prospect_id && (
                    <a data-testid={`inquiry-prospect-link-${q.id}`} href="/admin/prospects"
                      className="inline-flex items-center gap-1.5 text-xs font-bold bg-amber-100 text-amber-900 border border-amber-200 px-3 py-1.5 rounded-lg hover:bg-amber-200">
                      🎯 View in Prospects — {q.matched_prospect_company || "match"}
                    </a>
                  )}
                </div>
                {(q.wants_call || q.wants_proposal || q.wants_visit) && (
                  <p data-testid={`inquiry-wants-${q.id}`} className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900 font-medium">
                    They want:
                    {q.wants_call && <span className="ml-1.5 inline-block bg-white px-2 py-0.5 rounded text-xs">📞 Call</span>}
                    {q.wants_proposal && <span className="ml-1.5 inline-block bg-white px-2 py-0.5 rounded text-xs">📄 Proposal</span>}
                    {q.wants_visit && <span className="ml-1.5 inline-block bg-white px-2 py-0.5 rounded text-xs">🏢 Visit</span>}
                  </p>
                )}
                {(q.callback_date || q.callback_time) && (
                  <div data-testid={`inquiry-callback-${q.id}`} className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-emerald-900 font-medium flex flex-wrap items-center gap-x-2 gap-y-2">
                    <span>
                      📞 Callback requested:
                      {q.callback_date && <> {q.callback_date}</>}
                      {q.callback_time && <> at {q.callback_time}</>}
                      {q.callback_timezone && <span className="text-emerald-700/80 font-normal"> ({q.callback_timezone})</span>}
                    </span>
                    {q.callback_date && (
                      <button
                        data-testid={`inquiry-save-calendar-${q.id}`}
                        onClick={() => {
                          const ics = buildIcs({
                            date: q.callback_date,
                            time: q.callback_time || "09:00",
                            timezone: q.callback_timezone || "",
                            title: `Callback — ${q.company_name || q.contact_person || "Enterprise enquiry"}`,
                            description: `Enterprise callback with ${q.contact_person || ""}${q.job_title ? ", " + q.job_title : ""} from ${q.company_name || ""}.\n\nEmail: ${q.business_email || ""}${q.phone ? "\nPhone: " + q.phone : ""}${q.message ? "\n\nNotes:\n" + q.message : ""}`,
                            location: q.phone ? `Phone: ${q.phone}` : "Phone / video call",
                            durationMins: 30,
                          });
                          downloadIcs(ics, `callback-${(q.company_name || "enquiry").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${q.callback_date}.ics`);
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-bold bg-emerald-600 text-white px-2.5 py-1 rounded-md hover:bg-emerald-700 ml-auto"
                      >
                        <CalendarPlus size={13} /> Save to Calendar
                      </button>
                    )}
                  </div>
                )}
                {q.message && <p className="text-slate-600 whitespace-pre-wrap">{q.message}</p>}
                <p className="text-xs text-slate-400">Received {q.created_at?.slice(0, 10)}</p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {q.status !== "contacted" && <button onClick={() => setStatus(q.id, "contacted")} className={smallBtn} data-testid={`inquiry-contacted-${q.id}`}>Mark contacted</button>}
                  {q.status !== "closed" && <button onClick={() => setStatus(q.id, "closed")} className={smallBtn} data-testid={`inquiry-closed-${q.id}`}>Mark closed</button>}
                  {q.status !== "new" && <button onClick={() => setStatus(q.id, "new")} className={smallBtn}>Mark new</button>}
                  <button onClick={() => remove(q.id)} className={`${smallBtn} text-red-600 border-red-200 hover:bg-red-50`} data-testid={`inquiry-delete-${q.id}`}>Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminOrganisations() {
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/organisations");
      setOrgs(r.data);
      setSelected((cur) => cur ? (r.data.find((o) => o.id === cur.id) || null) : null);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (selected) return <OrgDetail org={selected} onBack={() => setSelected(null)} onOrgsChanged={load} />;

  return (
    <div data-testid="admin-organisations">
      <InquiriesPanel />
      <div className="flex items-center gap-3 mb-6">
        <h2 className="font-display font-bold text-xl flex items-center gap-2"><Building size={20} /> Organisations</h2>
        <button onClick={() => setShowCreate(true)} className="bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-xl hover:bg-emerald-800 transition-colors" data-testid="org-create-open">
          + Create organisation
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-5 max-w-2xl">Enterprise contracts: create an organisation, bulk-upload its employees (name, job title, email), manage seats and access, and broadcast updates to the whole company.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {orgs.map((o) => (
          <button key={o.id} onClick={() => setSelected(o)} className="text-left bg-white border border-slate-200 rounded-2xl p-5 hover:border-emerald-300 hover:shadow-sm transition-all" data-testid={`org-card-${o.id}`}>
            <div className="flex items-center gap-3">
              {o.logo_url ? <img src={o.logo_url} alt={o.name} className="h-11 w-11 rounded-lg object-contain border border-slate-100" />
                          : <div className="h-11 w-11 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400"><Building size={20} /></div>}
              <div className="min-w-0">
                <p className="font-bold truncate">{o.name}</p>
                <p className="text-xs text-slate-500">{o.seats_used}/{o.seat_limit} seats</p>
              </div>
              <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ${o.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>{o.status}</span>
            </div>
            {o.contact_name && <p className="text-xs text-slate-400 mt-2.5">{o.contact_name}{o.contact_email && ` • ${o.contact_email}`}</p>}
          </button>
        ))}
        {orgs.length === 0 && <p className="text-sm text-slate-400 py-8 col-span-full text-center">No organisations yet — create your first enterprise client.</p>}
      </div>
      {showCreate && <OrgFormModal onSaved={load} onClose={() => setShowCreate(false)} />}
    </div>
  );
}
